import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Plus, 
  Trash2,
  UserCheck,
  Pencil,
  HandHelping,
  Search,
  X,
  Users
} from "lucide-react";

const TasksPage = ({ tasks, categories, assets, employees, onTaskCreated, onTaskDeleted, api }) => {
  const navigate = useNavigate();
  const [newTask, setNewTask] = useState({ title: "", category: "", description: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [selectedTaskForAssets, setSelectedTaskForAssets] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAsset, setSelectedAsset] = useState("all");
  const [selectedOwner, setSelectedOwner] = useState("all");

  // Helper to get employee/asset info
  const getEmployeeInfo = (empId) => {
    return employees?.find(e => e.id === empId);
  };

  const getAssetInfo = (assetId) => {
    return assets?.find(a => a.id === assetId);
  };

  // Get list of Future Owners who have assignments
  const assignedOwners = employees?.filter(e => 
    tasks.some(t => t.assigned_to === e.id)
  ) || [];

  // Get list of Assets who are assigned to tasks
  const assignedAssets = assets?.filter(a => 
    tasks.some(t => t.assigned_assets?.includes(a.id))
  ) || [];

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = searchQuery === "" || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      task.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || task.category === selectedCategory;
    const matchesStatus = selectedStatus === "all" || task.status === selectedStatus;
    const matchesAsset = selectedAsset === "all" || 
      (selectedAsset === "unassigned" ? (!task.assigned_assets || task.assigned_assets.length === 0) : task.assigned_assets?.includes(selectedAsset));
    const matchesOwner = selectedOwner === "all" || 
      (selectedOwner === "unassigned" ? !task.assigned_to : task.assigned_to === selectedOwner);
    
    return matchesSearch && matchesCategory && matchesStatus && matchesAsset && matchesOwner;
  });

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.category) {
      toast.error("Please fill in title and category");
      return;
    }
    setIsCreating(true);
    try {
      await api.post("/tasks", newTask);
      toast.success("Responsibility created successfully!");
      setNewTask({ title: "", category: "", description: "" });
      setNewCategory("");
      setDialogOpen(false);
      onTaskCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create responsibility");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Responsibility deleted");
      onTaskDeleted();
    } catch (error) {
      toast.error("Failed to delete responsibility");
    }
  };

  const handleEditTask = (task) => {
    setEditingTask({ ...task });
    setEditDialogOpen(true);
  };

  const handleUpdateTask = async () => {
    if (!editingTask.title || !editingTask.category) {
      toast.error("Please fill in title and category");
      return;
    }
    setIsEditing(true);
    try {
      await api.put(`/tasks/${editingTask.id}`, {
        title: editingTask.title,
        category: editingTask.category,
        description: editingTask.description || ""
      });
      toast.success("Responsibility updated successfully!");
      setEditDialogOpen(false);
      setEditingTask(null);
      onTaskCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update responsibility");
    } finally {
      setIsEditing(false);
    }
  };

  const handleOpenAssetDialog = (task) => {
    setSelectedTaskForAssets(task);
    setAssetDialogOpen(true);
  };

  const handleAssignAsset = async (taskId, assetUserId) => {
    try {
      await api.post(`/tasks/${taskId}/assets`, { task_id: taskId, asset_user_id: assetUserId });
      toast.success("Asset assigned!");
      onTaskCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign asset");
    }
  };

  const handleRemoveAsset = async (taskId, assetUserId) => {
    try {
      await api.delete(`/tasks/${taskId}/assets/${assetUserId}`);
      toast.success("Asset removed");
      onTaskCreated();
    } catch (error) {
      toast.error("Failed to remove asset");
    }
  };

  const handleQuickAssign = async (taskId, employeeId, employeeName) => {
    try {
      await api.post("/tasks/assign", { task_id: taskId, employee_id: employeeId });
      toast.success(`Assigned to ${employeeName}!`);
      onTaskCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign responsibility");
    }
  };

  const allCategories = [...new Set([...categories, ...(newCategory ? [newCategory] : [])])];

  return (
    <div className="animate-in" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Responsibilities
          </h1>
          <p className="text-muted-foreground">Manage and create responsibilities for your future owners.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full px-6" data-testid="create-task-button">
              <Plus className="w-5 h-5 mr-2" />
              Create Responsibility
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Responsibility</DialogTitle>
              <DialogDescription>Add a new responsibility for your future owners to choose.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Responsibility Title</Label>
                <Input
                  id="title"
                  placeholder="Enter responsibility title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter responsibility description (optional)"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="min-h-[100px]"
                  data-testid="task-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={newTask.category}
                  onValueChange={(value) => setNewTask({ ...newTask, category: value })}
                >
                  <SelectTrigger data-testid="task-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    {newCategory && !categories.includes(newCategory) && (
                      <SelectItem value={newCategory}>{newCategory} (new)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Or type a new category"
                  value={newCategory}
                  onChange={(e) => {
                    setNewCategory(e.target.value);
                    setNewTask({ ...newTask, category: e.target.value });
                  }}
                  className="mt-2"
                  data-testid="new-category-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="rounded-full">Cancel</Button>
              </DialogClose>
              <Button
                onClick={handleCreateTask}
                disabled={isCreating}
                className="rounded-full"
                data-testid="submit-task-button"
              >
                {isCreating ? "Creating..." : "Create Responsibility"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-secondary/30 rounded-lg">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search responsibilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-8"
            data-testid="search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[130px] h-9" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[110px] h-9" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger className="w-[150px] h-9" data-testid="filter-owner">
            <SelectValue placeholder="Future Owner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Owners</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {assignedOwners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedAsset} onValueChange={setSelectedAsset}>
          <SelectTrigger className="w-[130px] h-9" data-testid="filter-asset">
            <SelectValue placeholder="Asset" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assets</SelectItem>
            <SelectItem value="unassigned">No Assets</SelectItem>
            {assignedAssets.map((asset) => (
              <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto">
          <Badge variant="outline" className="text-muted-foreground">
            {filteredTasks.length} of {tasks.length}
          </Badge>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <Card className="border-border/50 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {tasks.length === 0 ? "No Responsibilities Yet" : "No Results Found"}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {tasks.length === 0 
                ? "Create your first responsibility to get started."
                : "Try adjusting your search or filters."}
            </p>
            {(searchQuery || selectedCategory !== "all" || selectedStatus !== "all" || selectedOwner !== "all" || selectedAsset !== "all") && (
              <Button 
                variant="outline" 
                onClick={() => { setSearchQuery(""); setSelectedCategory("all"); setSelectedStatus("all"); setSelectedOwner("all"); setSelectedAsset("all"); }}
                className="rounded-full"
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((task) => (
            <Card 
              key={task.id} 
              className="task-card border-border/50 shadow-soft hover-lift cursor-pointer transition-all hover:border-primary/50" 
              data-testid={`task-card-${task.id}`}
              onClick={() => navigate(`/responsibility/${task.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                  <div className="flex items-center gap-2">
                    <div className={`status-dot ${task.status}`}></div>
                    <span className="text-xs text-muted-foreground capitalize">{task.status}</span>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{task.title}</h3>
                {task.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{task.description}</p>
                )}
                
                {/* Assigned Future Owner Section */}
                <div className="mb-3 p-3 rounded-lg bg-blue-50/50 border border-blue-100">
                  <span className="text-xs font-medium text-blue-700 flex items-center gap-1 mb-2">
                    <UserCheck className="w-3 h-3" /> Assigned To
                  </span>
                  {task.assigned_to ? (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-700 text-xs font-semibold">
                          {getEmployeeInfo(task.assigned_to)?.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-blue-800">
                        {getEmployeeInfo(task.assigned_to)?.name || 'Unknown'}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-blue-600/70 italic">Not assigned yet</p>
                  )}
                </div>
                
                {/* Assets Section */}
                <div className="mb-3 p-3 rounded-lg bg-orange-50/50 border border-orange-100" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-orange-700 flex items-center gap-1">
                      <HandHelping className="w-3 h-3" /> Assets
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                      onClick={(e) => { e.stopPropagation(); handleOpenAssetDialog(task); }}
                      data-testid={`manage-assets-${task.id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  {task.assigned_assets && task.assigned_assets.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {task.assigned_assets.map(assetId => {
                        const asset = getAssetInfo(assetId);
                        if (!asset) return null;
                        return (
                          <div 
                            key={assetId}
                            className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs group"
                          >
                            <span>{asset.name}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRemoveAsset(task.id, assetId); }}
                              className="ml-1 opacity-0 group-hover:opacity-100 hover:text-red-600 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-orange-600/70 italic">No assets assigned</p>
                  )}
                </div>

                {/* Interested Future Owners Section */}
                <div className="mb-4 p-3 rounded-lg bg-purple-50/50 border border-purple-200" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-purple-700 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Interested
                    </span>
                    <Badge 
                      variant={task.interested_employees.length > 0 ? "default" : "secondary"}
                      className={`text-xs px-2 py-0.5 ${task.interested_employees.length > 0 ? 'bg-purple-600 hover:bg-purple-600' : ''}`}
                    >
                      {task.interested_employees.length}
                    </Badge>
                  </div>
                  {task.interested_employees.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {task.interested_employees.map(empId => {
                        const employee = getEmployeeInfo(empId);
                        if (!employee) return null;
                        const isCurrentlyAssigned = task.assigned_to === empId;
                        return (
                          <div 
                            key={empId}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs group ${
                              isCurrentlyAssigned 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                              isCurrentlyAssigned ? 'bg-green-200' : 'bg-purple-200'
                            }`}>
                              <span className={`text-[10px] font-semibold ${
                                isCurrentlyAssigned ? 'text-green-800' : 'text-purple-800'
                              }`}>
                                {employee.name?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <span>{employee.name}</span>
                            {isCurrentlyAssigned ? (
                              <span className="text-[10px] text-green-600 ml-1">(Assigned)</span>
                            ) : !task.assigned_to && (
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleQuickAssign(task.id, empId, employee.name); 
                                }}
                                className="ml-1 px-2 py-0.5 rounded-full bg-purple-600 text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 hover:bg-purple-700 transition-all"
                                data-testid={`quick-assign-${task.id}-${empId}`}
                              >
                                Assign
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-purple-600/70 italic">No one interested yet</p>
                  )}
                </div>

                <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                      onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Responsibility Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Responsibility</DialogTitle>
            <DialogDescription>Update the responsibility details.</DialogDescription>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Responsibility Title</Label>
                <Input
                  id="edit-title"
                  placeholder="Enter responsibility title"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  data-testid="edit-task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Enter responsibility description (optional)"
                  value={editingTask.description || ""}
                  onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  className="min-h-[100px]"
                  data-testid="edit-task-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select
                  value={editingTask.category}
                  onValueChange={(value) => setEditingTask({ ...editingTask, category: value })}
                >
                  <SelectTrigger data-testid="edit-task-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Or type a new category"
                  onChange={(e) => {
                    if (e.target.value) {
                      setEditingTask({ ...editingTask, category: e.target.value });
                    }
                  }}
                  className="mt-2"
                  data-testid="edit-new-category-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleUpdateTask}
              disabled={isEditing}
              className="rounded-full"
              data-testid="update-task-button"
            >
              {isEditing ? "Updating..." : "Update Responsibility"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Asset Dialog */}
      <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Assign Assets</DialogTitle>
            <DialogDescription>
              {selectedTaskForAssets && `Select people to help with "${selectedTaskForAssets.title}"`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {assets && assets.length > 0 ? (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {assets.map(asset => {
                    const isAssigned = selectedTaskForAssets?.assigned_assets?.includes(asset.id);
                    return (
                      <div 
                        key={asset.id} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${isAssigned ? 'bg-orange-50 border border-orange-200' : 'bg-secondary/30 hover:bg-secondary/50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAssigned ? 'bg-orange-200' : 'bg-orange-100'}`}>
                            <HandHelping className={`w-5 h-5 ${isAssigned ? 'text-orange-700' : 'text-orange-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{asset.name}</p>
                            <p className="text-xs text-muted-foreground">{asset.email}</p>
                            {asset.can_help_with && asset.can_help_with.length > 0 && (
                              <p className="text-xs text-orange-600 mt-1">
                                Can help with {asset.can_help_with.length} responsibilities
                              </p>
                            )}
                          </div>
                        </div>
                        {isAssigned ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-100"
                            onClick={() => {
                              handleRemoveAsset(selectedTaskForAssets.id, asset.id);
                              setSelectedTaskForAssets({
                                ...selectedTaskForAssets,
                                assigned_assets: selectedTaskForAssets.assigned_assets.filter(id => id !== asset.id)
                              });
                            }}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            className="rounded-full bg-orange-500 hover:bg-orange-600"
                            onClick={() => {
                              handleAssignAsset(selectedTaskForAssets.id, asset.id);
                              setSelectedTaskForAssets({
                                ...selectedTaskForAssets,
                                assigned_assets: [...(selectedTaskForAssets.assigned_assets || []), asset.id]
                              });
                            }}
                          >
                            Assign
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8">
                <HandHelping className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No assets available.</p>
                <p className="text-sm text-muted-foreground mt-1">Go to Assets page to mark users as assets first.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="rounded-full">Done</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
